// 沿用 lingzhi-clipper/src/shared/citations.ts（同一个服务端、同一套协议），只改了 import。
import { LIMITS } from './limits'
import type { Reference } from './reference'

/**
 * 回答里的引用。服务端默认开启引用，正文里会夹两种自闭合标签（属性值已做 HTML 转义，所以值里不会出现 `>` 和 `"`）：
 *   <kb doc="标题" chunk_id="分块 ID" kb_id="知识库 ID" />
 *   <web url="https://…" title="标题" />
 * 这里把它们变成编号角标所需的数据，或者干脆去掉。认不出的写法当作没有引用。
 */

export interface CitationSource {
  kind: 'kb' | 'web'
  title: string
  /** 知识库来源：文档 ID 与所在知识库，两个都有才拼得出「在灵智原生打开」的地址。 */
  knowledgeId?: string
  kbId?: string
  /** 网页来源的地址（只会是 http(s)）。 */
  url?: string
}

/** 回答里的一处引用：指向第几个来源（角标上的编号是 source + 1），以及这一处引用的原文片段。 */
export interface Cite {
  source: number
  snippet: string
}

export interface Cited {
  /** 引用标签已换成 `[n](lingzhi-cite:暗号:i)` 的 Markdown。 */
  markdown: string
  sources: CitationSource[]
  cites: Cite[]
  /**
   * 这一次转换的暗号：只有带着它的 `lingzhi-cite:` 链接才会被认成角标（见 `citeIndexOf`）。
   * 模型或网页文字里自己写的 `lingzhi-cite:`——不管直接写，还是用字符实体、反斜杠转义、引用式链接、拆开标签拼出来——
   * 都猜不到它，所以伪造不出角标。
   */
  nonce: string
}

export const CITE_SCHEME = 'lingzhi-cite:'

/** 96 位随机数，十六进制。 */
function newNonce(): string {
  return Array.from(crypto.getRandomValues(new Uint8Array(12)), (byte) => byte.toString(16).padStart(2, '0')).join('')
}

const TAG = /<(kb|web)\b[^>]*>/gi
const ENTITIES: Record<string, string> = { amp: '&', lt: '<', gt: '>', quot: '"', apos: "'" }

function unescapeHtml(value: string): string {
  return value.replace(/&(?:#(\d{1,7})|#x([\da-f]{1,6})|(amp|lt|gt|quot|apos));/gi, (_all, dec?: string, hex?: string, name?: string) => {
    if (name) return ENTITIES[name.toLowerCase()]!
    const code = dec ? Number(dec) : Number.parseInt(hex!, 16)
    return code > 0 && code <= 0x10ffff ? String.fromCodePoint(code) : ''
  })
}

/** 按顺序逐个读出 `名="值"`：值被前一个属性整段吃掉，标题里写 `chunk_id=` 之类的字样也骗不了后面的属性。 */
function attributes(tag: string): Record<string, string> {
  const found: Record<string, string> = {}
  for (const match of tag.matchAll(/([a-z_][\w-]*)\s*=\s*"([^"]*)"/gi)) {
    found[match[1]!.toLowerCase()] ??= unescapeHtml(match[2]!)
  }
  return found
}

/** 能解析、且协议是 http(s)。 */
export const isHttpUrl = (value: string): boolean => {
  try {
    const { protocol } = new URL(value)
    return protocol === 'http:' || protocol === 'https:'
  } catch {
    return false
  }
}

/** 片段只留纯文字：压成单行空白并截断。 */
export function clipSnippet(value: unknown): string {
  if (typeof value !== 'string') return ''
  const flat = value.replace(/\s+/g, ' ').trim()
  return flat.length > LIMITS.snippetChars ? `${flat.slice(0, LIMITS.snippetChars)}…` : flat
}

/** 去掉所有引用标签（总结、翻译、复制出去的文字里不该出现它们）。 */
export function stripCitations(text: string): string {
  return text.replace(TAG, '')
}

/**
 * 流式输出时，尾部可能是还没收完整的标签（`<`、`<k`、`<kb doc="…`）。把这一小段先扣下，等标签完整了再显示；
 * 流结束后不再调用它，剩下的字符照原样显示。
 */
export function holdBackPartialTag(text: string): string {
  const open = text.lastIndexOf('<')
  if (open < 0) return text
  const tail = text.slice(open)
  return /^<(?:k|w|we)?$/i.test(tail) || /^<(?:kb|web)(?:\s[^>]*)?$/i.test(tail) ? text.slice(0, open) : text
}

/** 一处引用都没有时，写进历史的引用条数。 */
const KEPT_WITHOUT_CITES = 6

/**
 * 写进本机历史的引用：回答里引用到的分块（刷新之后角标的弹层、「依据」列表要用）；
 * 一处都引用不到时，留前几条当作依据。整段原文太占地方，调用方还要再瘦身。
 */
export function citedReferences(text: string, references: readonly Reference[]): Reference[] {
  const cited = new Set<string>()
  for (const tag of text.matchAll(TAG)) {
    if (tag[1]!.toLowerCase() !== 'kb') continue
    const chunkId = attributes(tag[0]).chunk_id
    if (chunkId) cited.add(chunkId)
  }
  const picked = references.filter((ref) => typeof ref.id === 'string' && cited.has(ref.id))
  return (picked.length > 0 ? picked : references.slice(0, KEPT_WITHOUT_CITES)).slice(0, LIMITS.references)
}

/** 没有行内引用时（引用被关闭、旧历史），回答下方的「依据」列表：按文档去重，保持原有顺序。 */
export function sourcesOf(references: readonly Reference[]): CitationSource[] {
  const seen = new Set<string>()
  const sources: CitationSource[] = []
  for (const ref of references) {
    const title = [ref.knowledge_title, ref.knowledge_filename].find((v): v is string => typeof v === 'string' && v.trim() !== '')
    if (!title) continue
    const knowledgeId = typeof ref.knowledge_id === 'string' && ref.knowledge_id ? ref.knowledge_id : undefined
    const key = knowledgeId ?? `title:${title}`
    if (seen.has(key)) continue
    seen.add(key)
    const kbId = typeof ref.knowledge_base_id === 'string' && ref.knowledge_base_id ? ref.knowledge_base_id : undefined
    sources.push({ kind: 'kb', title: title.trim(), ...(knowledgeId && kbId ? { knowledgeId, kbId } : {}) })
  }
  return sources
}

/**
 * 把正文里的引用标签换成 `[n](lingzhi-cite:暗号:i)`，并给出来源与引用点。
 * 编号按文档（没有文档 ID 就按标题、网页按地址）首次出现的顺序；同一个分块多次引用共用一个引用点。
 * 链接里带着本次的暗号（默认随机，测试可以指定），正文里别处写的 `lingzhi-cite:` 因为没有暗号，不会变成角标。
 */
export function withCitations(text: string, references: readonly Reference[] = [], nonce: string = newNonce()): Cited {
  const sources: CitationSource[] = []
  const sourceOf = new Map<string, number>()
  const cites: Cite[] = []
  const citeOf = new Map<string, number>()
  const chunks = new Map<string, Reference>()
  for (const ref of references) if (typeof ref.id === 'string' && ref.id) chunks.set(ref.id, ref)

  const sourceIndex = (key: string, source: CitationSource): number => {
    let index = sourceOf.get(key)
    if (index === undefined) {
      index = sources.push(source) - 1
      sourceOf.set(key, index)
    }
    return index
  }

  const markdown = text.replace(TAG, (tag, name: string) => {
    const attrs = attributes(tag)
    let source: number
    let snippet = ''
    let chunkKey = ''
    if (name.toLowerCase() === 'web') {
      const url = attrs.url ?? ''
      if (!isHttpUrl(url)) return ''
      source = sourceIndex(`web:${url}`, { kind: 'web', title: attrs.title?.trim() || new URL(url).hostname, url })
    } else {
      const chunkId = attrs.chunk_id ?? ''
      const ref = chunks.get(chunkId)
      const title = [attrs.doc, ref?.knowledge_title, ref?.knowledge_filename].find((v): v is string => typeof v === 'string' && v.trim() !== '')
      if (!title) return ''
      const knowledgeId = typeof ref?.knowledge_id === 'string' && ref.knowledge_id ? ref.knowledge_id : undefined
      const kbId = attrs.kb_id || (typeof ref?.knowledge_base_id === 'string' ? ref.knowledge_base_id : '')
      source = sourceIndex(knowledgeId ? `kb:${knowledgeId}` : `title:${title.trim()}`, {
        kind: 'kb',
        title: title.trim(),
        ...(knowledgeId && kbId ? { knowledgeId, kbId } : {}),
      })
      snippet = clipSnippet(ref?.content)
      chunkKey = chunkId
    }
    const citeKey = `${source}|${chunkKey}`
    let cite = citeOf.get(citeKey)
    if (cite === undefined) {
      cite = cites.push({ source, snippet }) - 1
      citeOf.set(citeKey, cite)
    }
    return `[${source + 1}](${CITE_SCHEME}${nonce}:${cite})`
  })
  return { markdown, sources, cites, nonce }
}

/** 从链接地址里取出引用点序号；不是我们这一次生成的写法（暗号对不上、格式不对）就返回 null。 */
export function citeIndexOf(href: string | undefined, nonce: string | undefined): number | null {
  if (!href || !nonce) return null
  const prefix = `${CITE_SCHEME}${nonce}:`
  if (!href.startsWith(prefix)) return null
  const rest = href.slice(prefix.length)
  return /^\d{1,4}$/.test(rest) ? Number(rest) : null
}
